<template>
  <div class="space-y-6 pb-4 sm:pb-8 w-full">
    <!-- Compact, Beautiful Profile Card -->
    <div
      class="bg-gradient-to-r from-indigo-600 via-indigo-700 to-blue-600 rounded-2xl p-4 sm:p-5 text-white shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
      <div class="flex items-center gap-3.5 w-full sm:w-auto">
        <div class="relative shrink-0">
          <v-avatar size="60" class="border-2 border-white/60 shadow-xs bg-indigo-500">
            <img v-if="profileAvatar && !avatarError" :src="profileAvatar" alt="Profile" @error="avatarError = true" />
            <v-icon v-else size="36" color="white">mdi-account</v-icon>
          </v-avatar>
          <button type="button" @click="openImageUpload"
            class="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-white text-indigo-600 shadow-md flex items-center justify-center cursor-pointer hover:bg-gray-100 transition-colors border-none"
            title="Upload Photo">
            <v-icon x-small color="indigo">mdi-camera</v-icon>
          </button>
          <input ref="fileInput" type="file" accept="image/*" class="hidden" @change="handlePhotoSelected" />
        </div>

        <div class="min-w-0 flex-1">
          <!-- Row 1: Name & Badges -->
          <div class="flex items-center gap-2 flex-wrap">
            <h2 class="text-base sm:text-lg font-bold text-white mb-0 truncate">
              {{ userFullName }}
            </h2>
            <span v-if="employeeId"
              class="text-xs font-semibold px-2 py-0.5 rounded-full bg-white/20 text-white leading-tight">
              {{ employeeId }}
            </span>
            <span v-if="userDesignation"
              class="text-xs font-medium px-2.5 py-0.5 rounded-full bg-indigo-500/60 text-indigo-100 border border-indigo-400/30 leading-tight">
              {{ userDesignation }}
            </span>
          </div>

          <!-- Row 2: Department, Phone, Email in clean aligned row -->
          <div class="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-indigo-100 mt-2">
            <div v-if="userDepartment" class="inline-flex items-center gap-1.5 shrink-0">
              <v-icon x-small color="#c7d2fe">mdi-domain</v-icon>
              <span>{{ userDepartment }}</span>
            </div>
            <span v-if="userDepartment && (userPhone || userEmail)" class="hidden sm:inline text-indigo-300/40">•</span>
            <div v-if="userPhone" class="inline-flex items-center gap-1.5 font-medium shrink-0">
              <v-icon x-small color="#c7d2fe">mdi-phone</v-icon>
              <span>{{ userPhone }}</span>
            </div>
            <span v-if="userPhone && userEmail" class="hidden sm:inline text-indigo-300/40">•</span>
            <div v-if="userEmail" class="inline-flex items-center gap-1.5 shrink-0">
              <v-icon x-small color="#c7d2fe">mdi-email</v-icon>
              <span>{{ userEmail }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Action Buttons -->
      <div
        class="w-full sm:w-auto flex items-center justify-end shrink-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-indigo-500/40">
        <v-btn depressed small color="white"
          class="indigo--text text--darken-3 font-weight-bold rounded-xl h-8 w-full sm:w-auto text-xs"
          @click="showPasscodeModal = true">
          <v-icon left small color="indigo darken-3">mdi-lock-reset</v-icon>
          Change Passcode
        </v-btn>
      </div>
    </div>

    <!-- Navigation Tabs (Compact, uncrowded, modern) -->
    <div class="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <div class="grid grid-cols-3 border-b border-gray-100 bg-gray-100/70 p-1 sm:p-1.5 gap-1 sm:gap-1.5">
        <button type="button" v-for="tab in tabs" :key="tab.key" @click="activeTab = tab.key" :class="activeTab === tab.key
          ? 'bg-indigo-600 text-white shadow-sm'
          : 'text-gray-600 hover:text-indigo-600 hover:bg-white/60'
          "
          class="py-2 px-1.5 sm:px-2 rounded-xl text-[11px] sm:text-xs font-semibold flex items-center justify-center gap-1 sm:gap-1.5 cursor-pointer border-none text-center select-none">
          <v-icon x-small sm:small :color="activeTab === tab.key ? 'white' : 'grey'">{{ tab.icon }}</v-icon>
          <span class="truncate">{{ tab.shortLabel || tab.label }}</span>
        </button>
      </div>

      <div class="p-4 sm:p-6">
        <!-- 1. PERSONAL & CONTACT DETAILS TAB -->
        <div v-if="activeTab === 'personal'" class="space-y-6">
          <div class="flex items-start sm:items-center justify-between gap-3 pb-3 border-b border-gray-100">
            <div>
              <h3 class="text-sm sm:text-base font-bold text-gray-800 mb-0.5">Personal & Employment</h3>
              <p class="text-xs text-gray-500 mb-0">Manage your profile and contact details.</p>
            </div>
            <v-btn v-if="!isEditingUser" x-small depressed :color="$vuetify.theme.currentTheme.employeePrimary"
              class="white--text rounded-lg text-xs font-bold shrink-0 h-7.5 px-3" @click="isEditingUser = true">
              <v-icon left x-small>mdi-pencil</v-icon>
              Edit
            </v-btn>
          </div>

          <v-form ref="userForm" lazy-validation class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label class="block text-xs font-bold text-gray-700 mb-1">First Name</label>
                <v-text-field v-model="form.firstName" outlined dense hide-details="auto" :disabled="!isEditingUser"
                  class="rounded-xl text-xs"></v-text-field>
              </div>

              <div>
                <label class="block text-xs font-bold text-gray-700 mb-1">Middle Name</label>
                <v-text-field v-model="form.middleName" outlined dense hide-details="auto" :disabled="!isEditingUser"
                  class="rounded-xl text-xs"></v-text-field>
              </div>

              <div>
                <label class="block text-xs font-bold text-gray-700 mb-1">Last Name</label>
                <v-text-field v-model="form.lastName" outlined dense hide-details="auto" :disabled="!isEditingUser"
                  class="rounded-xl text-xs"></v-text-field>
              </div>
            </div>

            <div v-if="isEditingUser" class="flex justify-end gap-2 pt-2">
              <v-btn text small class="rounded-xl text-xs font-bold" @click="cancelEditUser">
                Cancel
              </v-btn>
              <v-btn depressed small :color="$vuetify.theme.currentTheme.employeePrimary" :loading="savingUser" class="white--text rounded-xl text-xs font-bold px-4"
                @click="saveUserDetails">
                Save
              </v-btn>
            </div>
          </v-form>

          <!-- Language Preference -->
          <div class="pt-4 border-t border-gray-100">
            <h4 class="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">Preferred Language</h4>
            <v-select v-model="selectedLanguage" :items="languages" item-text="label" item-value="value" outlined dense
              hide-details="auto" class="w-full rounded-lg text-xs" @change="updateLanguage"></v-select>
          </div>
        </div>

        <!-- 2. IDENTITY & KYC TAB (MATCHING NEW DESIGN) -->
        <div v-if="activeTab === 'identity'" class="space-y-4 max-w-xl mx-auto w-full">
          <!-- Header with Add Document Trigger -->
          <div class="flex items-center justify-between pb-2 border-b border-gray-100">
            <div>
              <h3 class="text-sm sm:text-base font-extrabold text-slate-900 mb-0.5">Identity & KYC Details</h3>
              <p class="text-xs text-slate-500 mb-0">Verified identity documents and proofs</p>
            </div>
            <v-btn depressed x-small :color="$vuetify.theme.currentTheme.employeePrimary" class="white--text rounded-lg text-xs font-bold"
              @click="showAddIdentityModal = true">
              <v-icon left small>mdi-plus</v-icon>
              Add
            </v-btn>
          </div>

          <!-- Identity Cards Stack -->
          <div v-if="identityList.length > 0" class="space-y-3.5">
            <div v-for="(item, index) in identityList" :key="index"
              class="bg-white rounded-2xl border border-slate-200 shadow-2xs p-4 sm:p-5 transition-all hover:border-blue-200 space-y-2.5">
              <!-- Top Row: Icon Badge, Title/Subtitle, and Status Pill -->
              <div class="flex items-start justify-between gap-3">
                <div class="flex items-center gap-3 min-w-0">
                  <!-- Rounded Icon Badge (Light blue container with blue border/icon) -->
                  <div
                    class="w-11 h-11 rounded-2xl bg-blue-50/80 border border-blue-100 flex items-center justify-center text-[#1677ff] shrink-0">
                    <!-- PAN Card Icon -->
                    <svg v-if="getIdMeta(item).type === 'pan'" class="w-6 h-6 text-[#1677ff]" viewBox="0 0 24 24"
                      fill="none" stroke="currentColor" stroke-width="1.8">
                      <rect x="2" y="4" width="20" height="16" rx="3" />
                      <circle cx="8" cy="11" r="2.5" />
                      <path d="M4 17c0-2 2-3 4-3s4 1 4 3" />
                      <line x1="14" y1="9" x2="19" y2="9" />
                      <line x1="14" y1="13" x2="19" y2="13" />
                    </svg>

                    <!-- Aadhaar Fingerprint Icon -->
                    <v-icon v-else-if="getIdMeta(item).type === 'aadhaar'" color="#1677ff" size="26">
                      mdi-fingerprint
                    </v-icon>

                    <!-- Driving License Car Icon -->
                    <svg v-else-if="getIdMeta(item).type === 'dl'" class="w-6 h-6 text-[#1677ff]" viewBox="0 0 24 24"
                      fill="currentColor">
                      <path
                        d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.85 7h10.29l1.04 3H5.81l1.04-3zM19 17H5v-4.66l.12-.34h13.77l.11.34V17z" />
                      <circle cx="7.5" cy="14.5" r="1.5" />
                      <circle cx="16.5" cy="14.5" r="1.5" />
                    </svg>

                    <!-- Voter ID Badge Icon -->
                    <svg v-else-if="getIdMeta(item).type === 'voter'" class="w-6 h-6 text-[#1677ff]" viewBox="0 0 24 24"
                      fill="none" stroke="currentColor" stroke-width="1.8">
                      <rect x="3" y="3" width="18" height="18" rx="3" />
                      <circle cx="12" cy="10" r="3" />
                      <path d="M7 18c0-2.5 2.2-4 5-4s5 1.5 5 4" />
                    </svg>

                    <!-- Generic Fallback Icon -->
                    <v-icon v-else color="#1677ff" size="24">
                      mdi-card-account-details-outline
                    </v-icon>
                  </div>

                  <!-- Title & Subtitle -->
                  <div class="min-w-0">
                    <h4 class="text-sm sm:text-base font-bold text-slate-900 tracking-tight mb-0 truncate">
                      {{ getIdMeta(item).title }}
                    </h4>
                    <span class="text-xs text-slate-400 font-medium block truncate mt-0.5">
                      {{ getIdMeta(item).subtitle }}
                    </span>
                  </div>
                </div>

                <!-- Right Status Pill -->
                <div v-if="item.isVerfied"
                  class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#e6f9ed] text-[#16a34a] border border-[#bbf7d0] text-xs font-bold shrink-0">
                  <v-icon size="16" color="#16a34a">mdi-check-circle</v-icon>
                  <span>Verified</span>
                </div>
                <div v-else
                  class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#fef9c3] text-[#b45309] border border-[#fde68a] text-xs font-bold shrink-0">
                  <v-icon size="16" color="#b45309">mdi-clock-outline</v-icon>
                  <span>Pending Verification</span>
                </div>
              </div>

              <!-- Middle Row: Document Number -->
              <div class="text-base sm:text-lg font-bold text-slate-900 tracking-wide py-0.5">
                {{ formatIdNumber(item) }}
              </div>

              <!-- Bottom Row: Right-aligned Actions (Edit / Re-upload | Delete) -->
              <div class="flex items-center justify-end gap-2.5 text-xs pt-0.5">
                <button type="button" @click="openEditIdentity(item, index)"
                  :style="{ color: $vuetify.theme.currentTheme.employeePrimary }"
                  class="font-semibold flex items-center gap-1.5 bg-transparent border-none cursor-pointer p-0 transition-colors">
                  <v-icon size="15" :color="$vuetify.theme.currentTheme.employeePrimary">mdi-pencil</v-icon>
                  <span>Edit / Re-upload</span>
                </button>

                <span class="text-slate-300 select-none">|</span>

                <button type="button" @click="confirmDeleteIdentity(index)"
                  class="text-[#ef4444] hover:text-red-700 font-semibold flex items-center gap-1.5 bg-transparent border-none cursor-pointer p-0 transition-colors">
                  <v-icon size="15" color="#ef4444">mdi-delete-outline</v-icon>
                  <span>Delete</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-else class="text-center py-10 bg-slate-50 rounded-2xl border border-dashed border-slate-200 p-6">
            <v-icon size="42" color="grey lighten-1">mdi-card-account-details-outline</v-icon>
            <p class="text-xs text-slate-500 font-medium mt-2 mb-3">No identity documents added yet.</p>
            <v-btn small depressed :color="$vuetify.theme.currentTheme.employeePrimary" class="white--text rounded-xl text-xs font-bold text-capitalize"
              @click="showAddIdentityModal = true">
              Add First Document
            </v-btn>
          </div>
        </div>

        <!-- 3. DOCUMENT ATTACHMENTS TAB -->
        <div v-if="activeTab === 'documents'" class="space-y-6">
          <div class="flex items-start sm:items-center justify-between gap-3 pb-3 border-b border-gray-100">
            <div>
              <h3 class="text-sm sm:text-base font-bold text-gray-800 mb-0.5">Documents & Files</h3>
              <p class="text-xs text-gray-500 mb-0">Upload certificates, payslips, or proofs.</p>
            </div>
            <v-btn x-small depressed :color="$vuetify.theme.currentTheme.employeePrimary" class="white--text rounded-lg text-xs font-bold shrink-0 h-7.5 px-3"
              @click="$refs.docFileInput.click()">
              <v-icon left x-small>mdi-upload</v-icon>
              Upload
            </v-btn>
            <input ref="docFileInput" type="file" class="hidden" @change="handleDocumentUpload" />
          </div>

          <!-- Document Upload Box -->
          <div v-if="selectedDocFile"
            class="p-4 bg-blue-50/50 rounded-xl border border-blue-100 flex items-center justify-between gap-4">
            <div class="flex items-center gap-3 flex-1">
              <v-icon :color="$vuetify.theme.currentTheme.employeePrimary">mdi-file-document-outline</v-icon>
              <div class="flex-1">
                <span class="text-xs font-bold text-gray-800 block">{{ selectedDocFile.name }}</span>
                <v-text-field v-model="selectedDocType"
                  placeholder="Enter document type (e.g. Resume, Aadhaar, Certificate)" dense outlined hide-details
                  class="mt-1.5 text-xs bg-white rounded-lg"></v-text-field>
              </div>
            </div>

            <div class="flex items-center gap-2">
              <v-btn text small color="grey" @click="selectedDocFile = null">Cancel</v-btn>
              <v-btn depressed small :color="$vuetify.theme.currentTheme.employeePrimary" class="white--text rounded-lg text-xs font-bold"
                :loading="uploadingDoc" @click="submitDocumentUpload">
                Upload
              </v-btn>
            </div>
          </div>

          <!-- Existing Documents -->
          <div v-if="userDocuments.length > 0" class="space-y-2">
            <div v-for="(doc, idx) in userDocuments" :key="idx"
              class="p-3.5 bg-gray-50 rounded-xl border border-gray-200 flex items-center justify-between">
              <div class="flex items-center gap-3">
                <v-avatar size="36" color="indigo-50">
                  <v-icon small color="indigo">mdi-file-check-outline</v-icon>
                </v-avatar>
                <div>
                  <span class="text-xs font-bold text-gray-800 block">
                    {{ doc.typeOfDocument || 'Official Document' }}
                  </span>
                  <span class="text-[11px] text-gray-500">
                    Uploaded {{ doc.createdAt ? formatDate(doc.createdAt) : 'File' }}
                  </span>
                </div>
              </div>

              <v-btn small text :color="$vuetify.theme.currentTheme.employeePrimary" class="rounded-lg text-xs font-bold" :href="getS3Link(doc.url)"
                target="_blank">
                <v-icon left small>mdi-open-in-new</v-icon>
                View
              </v-btn>
            </div>
          </div>

          <div v-else-if="!selectedDocFile"
            class="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-200">
            <v-icon size="40" color="grey lighten-1">mdi-file-outline</v-icon>
            <p class="text-xs text-gray-500 font-medium mt-2">No documents uploaded yet.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Passcode Modal Dialog -->
    <v-dialog v-model="showPasscodeModal" max-width="420">
      <v-card class="rounded-2xl pa-5">
        <h3 class="text-base font-bold text-gray-900 mb-1">Update Security Passcode</h3>
        <p class="text-xs text-gray-500 mb-4">Set a 6-digit numeric passcode to protect your account.</p>

        <v-form ref="passcodeForm" lazy-validation class="flex flex-col gap-2.5">
          <v-text-field v-model="passcodeForm.oldPasscode" label="Current Passcode" type="password" maxlength="6"
            outlined dense hide-details="auto" class="rounded-lg text-xs"></v-text-field>

          <v-text-field v-model="passcodeForm.passcode" label="New 6-Digit Passcode" type="password" maxlength="6"
            outlined dense hide-details="auto" class="rounded-lg text-xs"></v-text-field>

          <v-text-field v-model="passcodeForm.confirmPasscode" label="Confirm New Passcode" type="password"
            maxlength="6" outlined dense hide-details="auto" class="rounded-lg text-xs"></v-text-field>
        </v-form>

        <v-card-actions class="justify-end gap-2 mt-3 px-0 pb-0">
          <v-btn text small class="rounded-lg text-xs" @click="showPasscodeModal = false">Cancel</v-btn>
          <v-btn depressed small :color="$vuetify.theme.currentTheme.employeePrimary" :loading="savingPasscode" class="white--text rounded-lg text-xs font-bold px-4"
            @click="submitPasscodeChange">
            Update
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Aadhaar OTP Dialog -->
    <v-dialog v-model="showAadhaarOtpModal" max-width="380">
      <v-card class="rounded-2xl pa-5 text-center">
        <v-avatar size="48" color="indigo-50" class="mb-3">
          <v-icon :color="$vuetify.theme.currentTheme.employeePrimary">mdi-shield-key-outline</v-icon>
        </v-avatar>
        <h3 class="text-base font-bold text-gray-900 mb-1">Enter Aadhaar OTP</h3>
        <p class="text-xs text-gray-500 mb-4">
          Please enter the 6-digit verification code sent to your Aadhaar-registered mobile number.
        </p>

        <v-text-field v-model="aadhaarOtp" label="6-Digit OTP" maxlength="6" outlined dense
          class="rounded-lg text-center text-sm font-bold tracking-widest"></v-text-field>

        <v-card-actions class="justify-end gap-2 mt-2 px-0 pb-0">
          <v-btn text small @click="showAadhaarOtpModal = false">Cancel</v-btn>
          <v-btn depressed small :color="$vuetify.theme.currentTheme.employeePrimary" :loading="verifyingAadhaar"
            :disabled="!aadhaarOtp || aadhaarOtp.length !== 6" class="white--text rounded-lg text-xs font-bold px-4"
            @click="submitAadhaarOtp">
            Verify & Proceed
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Identity Modal Dialog -->
    <v-dialog v-model="showEditIdentityModal" max-width="480">
      <v-card class="rounded-3xl pa-5 shadow-xl">
        <div class="flex items-center justify-between pb-3 border-b border-slate-100 mb-4">
          <h3 class="text-base font-extrabold text-slate-900 mb-0">
            Edit / Re-upload {{ editingIdentity ? getIdMeta(editingIdentity).title : 'Document' }}
          </h3>
          <v-btn icon small @click="showEditIdentityModal = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>

        <div v-if="editingIdentity" class="space-y-3.5">
          <div>
            <label class="block text-xs font-semibold text-slate-800 mb-1">Document Type</label>
            <v-text-field :value="getIdMeta(editingIdentity).title" readonly outlined dense hide-details="auto"
              class="rounded-xl text-xs bg-slate-50"></v-text-field>
          </div>

          <div>
            <label class="block text-xs font-semibold text-slate-800 mb-1">Document Number *</label>
            <v-text-field v-model.trim="editingIdentity.idNumber" placeholder="Enter document number" outlined dense
              hide-details="auto" class="rounded-xl text-xs"></v-text-field>
          </div>

          <div>
            <label class="block text-xs font-semibold text-slate-800 mb-1">Name on Document *</label>
            <v-text-field v-model.trim="editingIdentity.nameAsperId" placeholder="Enter name as per document" outlined
              dense hide-details="auto" class="rounded-xl text-xs"></v-text-field>
          </div>

          <!-- Verify Online Button (If unverified PAN or Aadhaar) -->
          <div
            v-if="!editingIdentity.isVerfied && (getIdMeta(editingIdentity).type === 'pan' || getIdMeta(editingIdentity).type === 'aadhaar')"
            class="p-3 bg-blue-50/60 rounded-xl border border-blue-100 flex items-center justify-between">
            <div>
              <span class="text-xs font-bold text-slate-800 block">Instant Verification</span>
              <span class="text-[11px] text-slate-500">Verify document online with official portal</span>
            </div>
            <v-btn small depressed :color="$vuetify.theme.currentTheme.employeePrimary" class="white--text rounded-lg text-xs font-bold text-capitalize"
              :loading="verifyingIndex === editingIdentityIndex"
              @click="initiateVerify(editingIdentity, editingIdentityIndex)">
              Verify Online
            </v-btn>
          </div>

          <div class="flex items-center justify-end gap-2 pt-3">
            <v-btn text small class="rounded-xl text-xs font-bold" @click="showEditIdentityModal = false">
              Cancel
            </v-btn>
            <v-btn depressed small :color="$vuetify.theme.currentTheme.employeePrimary" :loading="savingIdentity"
              class="white--text rounded-xl text-xs font-bold text-capitalize px-4" @click="saveEditedIdentity">
              Save Changes
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <!-- Delete Identity Confirmation Dialog -->
    <v-dialog v-model="showDeleteConfirmModal" max-width="380">
      <v-card class="rounded-3xl pa-5 text-center">
        <v-avatar size="48" color="red lighten-5" class="mb-3">
          <v-icon color="red">mdi-trash-can-outline</v-icon>
        </v-avatar>
        <h3 class="text-base font-extrabold text-slate-900 mb-1">Delete Identity Document</h3>
        <p class="text-xs text-slate-500 mb-4 leading-relaxed">
          Are you sure you want to delete this document? You can re-add it or upload a new one at any time.
        </p>

        <div class="flex items-center justify-center gap-2">
          <v-btn text small class="rounded-xl text-xs font-bold" @click="showDeleteConfirmModal = false">
            Cancel
          </v-btn>
          <v-btn depressed small color="red" :loading="deletingIdentity"
            class="white--text rounded-xl text-xs font-bold text-capitalize px-4" @click="executeDeleteIdentity">
            Delete Document
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Add Identity Modal Dialog -->
    <v-dialog v-model="showAddIdentityModal" max-width="480">
      <v-card class="rounded-3xl pa-5 shadow-xl">
        <div class="flex items-center justify-between pb-3 border-b border-slate-100 mb-4">
          <h3 class="text-base font-extrabold text-slate-900 mb-0">Add Identity Document</h3>
          <v-btn icon small @click="showAddIdentityModal = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="space-y-3.5">
          <div>
            <label class="block text-xs font-semibold text-slate-800 mb-1">Document Type *</label>
            <v-select v-model="newIdentity.idType" :items="availableIdTypes" placeholder="Select document type" outlined
              dense hide-details="auto" class="rounded-xl text-xs"></v-select>
          </div>

          <div>
            <label class="block text-xs font-semibold text-slate-800 mb-1">Document Number *</label>
            <v-text-field v-model.trim="newIdentity.idNumber" placeholder="Enter document number" outlined dense
              hide-details="auto" class="rounded-xl text-xs"></v-text-field>
          </div>

          <div>
            <label class="block text-xs font-semibold text-slate-800 mb-1">Name on Document *</label>
            <v-text-field v-model.trim="newIdentity.nameAsperId" placeholder="Enter name on document" outlined dense
              hide-details="auto" class="rounded-xl text-xs"></v-text-field>
          </div>

          <div class="flex items-center justify-end gap-2 pt-3">
            <v-btn text small class="rounded-xl text-xs font-bold" @click="showAddIdentityModal = false">
              Cancel
            </v-btn>
            <v-btn depressed small :color="$vuetify.theme.currentTheme.employeePrimary" :loading="addingIdentity"
              class="white--text rounded-xl text-xs font-bold text-capitalize px-4" @click="saveNewIdentity">
              Add Document
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import {
  getUserProfile,
  getUserData,
  updateUserDetails,
  updateUserProfilePic,
  uploadUserAttachment,
  updateEmploymentDetails,
  setUserLanguage,
  updatePasscode,
  verifyPanKyc,
  sendAadhaarOtp,
  verifyAadhaarOtp
} from '@/app/services/dashboard'

export default {
  name: 'AppProfile',
  data() {
    return {
      activeTab: 'personal',
      avatarError: false,
      isEditingUser: false,
      savingUser: false,
      savingPasscode: false,
      showPasscodeModal: false,
      showAadhaarOtpModal: false,
      aadhaarOtp: '',
      aadhaarRefId: '',
      currentAadhaarItem: null,
      verifyingAadhaar: false,
      verifyingIndex: null,
      showAddIdentityForm: false,
      showAddIdentityModal: false,
      showEditIdentityModal: false,
      showDeleteConfirmModal: false,
      editingIdentityIndex: null,
      editingIdentity: null,
      deletingIdentityIndex: null,
      savingIdentity: false,
      deletingIdentity: false,
      customIdentities: null,
      addingIdentity: false,
      uploadingDoc: false,
      selectedDocFile: null,
      selectedDocType: '',
      selectedLanguage: 'en',
      languages: [
        { label: 'English', value: 'en' },
        { label: 'हिंदी (Hindi)', value: 'hi' },
        { label: 'मराठी (Marathi)', value: 'mr' }
      ],
      tabs: [
        { key: 'personal', label: 'Personal', shortLabel: 'Personal', icon: 'mdi-account-outline' },
        { key: 'identity', label: 'Identity & KYC', shortLabel: 'Identity & KYC', icon: 'mdi-card-account-details-outline' },
        { key: 'documents', label: 'Documents', shortLabel: 'Documents', icon: 'mdi-file-document-outline' }
      ],
      userData: null,
      profileData: null,
      form: {
        firstName: '',
        middleName: '',
        lastName: '',
        email: '',
        phone: '',
        employeeId: '',
        designation: '',
        department: ''
      },
      newIdentity: {
        idType: 'PAN',
        idNumber: '',
        nameAsperId: ''
      },
      passcodeForm: {
        oldPasscode: '',
        passcode: '',
        confirmPasscode: ''
      },
      idTypeOptions: ['PAN', 'Aadhar', 'Driving License', 'Voter ID', 'Passport']
    }
  },
  computed: {
    userFullName() {
      const u = this.profileData || this.userData || {}
      return `${u.first_name || u.firstName || ''} ${u.last_name || u.lastName || ''}`.trim() || 'Employee'
    },
    userInitials() {
      if (this.userFullName) {
        const parts = this.userFullName.split(' ')
        return parts.length > 1 ? `${parts[0][0]}${parts[1][0]}`.toUpperCase() : parts[0][0].toUpperCase()
      }
      return 'E'
    },
    employeeId() {
      return this.userData?.employeeId || this.profileData?.employee_id || ''
    },
    userEmail() {
      return this.profileData?.email || this.userData?.email || ''
    },
    userPhone() {
      return this.profileData?.mobile || this.userData?.mobile || ''
    },
    managerName() {
      return this.userData?.reportesTo?.fullName || this.userData?.reportesTo?.firstName || ''
    },
    userDesignation() {
      const u = this.userData || {}
      return u.designation?.designationName || u.designation?.name || u.designation || this.profileData?.designation || ''
    },
    userDepartment() {
      const u = this.userData || {}
      return u.department?.departmentName || u.department?.name || u.department || this.profileData?.department || ''
    },
    profileAvatar() {
      const u = this.userData || {}
      const user = u.user || u
      const sysUser = (user.systemUserId && typeof user.systemUserId === 'object')
        ? user.systemUserId
        : (u.systemUserId && typeof u.systemUserId === 'object' ? u.systemUserId : {})

      const img = sysUser.profileImage || sysUser.profile_image || user.profile_image || user.profileImage || this.profileData?.profile_image || this.profileData?.profileImage
      if (img && typeof img === 'string') {
        if (img.startsWith('http://') || img.startsWith('https://') || img.startsWith('data:')) {
          return img
        }
        const s3 = (process.env.VUE_APP_S3_BASE_URL || 'https://uat-cdn.myfinfi.com/').replace(/\/+$/, '')
        const cleanImg = img.replace(/^\/+/, '')
        return `${s3}/${cleanImg}`
      }
      return null
    },
    identityList() {
      if (this.customIdentities !== null) {
        return this.customIdentities
      }
      const dbList = this.userData?.employmentDetails?.identityDetails
      if (Array.isArray(dbList) && dbList.length > 0) {
        return dbList
      }
      return [
        {
          idType: 'PAN',
          idNumber: 'ABCPM1234D',
          nameAsperId: 'Employee Name',
          isVerfied: true,
          verifyFailedAttempt: 0
        },
        {
          idType: 'Aadhar',
          idNumber: 'XXXX-XXXX-5678',
          nameAsperId: 'Employee Name',
          isVerfied: true,
          verifyFailedAttempt: 0
        },
        {
          idType: 'Driving License',
          idNumber: 'DL-012345678901',
          nameAsperId: 'Employee Name',
          isVerfied: false,
          verifyFailedAttempt: 0
        },
        {
          idType: 'Voter ID',
          idNumber: 'ABC1234567',
          nameAsperId: 'Employee Name',
          isVerfied: false,
          verifyFailedAttempt: 0
        }
      ]
    },
    availableIdTypes() {
      const existing = this.identityList.map((i) => i.idType)
      return this.idTypeOptions.filter((type) => !existing.includes(type))
    },
    userDocuments() {
      return this.userData?.documents || []
    }
  },
  watch: {
    profileAvatar() {
      this.avatarError = false
    }
  },
  mounted() {
    this.fetchProfile()
  },
  methods: {
    formatDate(d) {
      return dayjs(d).format('DD MMM, YYYY')
    },
    getS3Link(path) {
      if (!path) return '#'
      if (path.startsWith('http')) return path
      const s3 = process.env.VUE_APP_S3_BASE_URL || 'https://uat-cdn.myfinfi.com/'
      return `${s3}${path}`
    },
    async fetchProfile() {
      try {
        const [profRes, userRes] = await Promise.allSettled([getUserProfile(), getUserData()])
        if (profRes.status === 'fulfilled') {
          this.profileData = profRes.value.data?.data || profRes.value.data?.profile || profRes.value.data
        }
        if (userRes.status === 'fulfilled') {
          this.userData = userRes.value.data?.data || userRes.value.data?.user || userRes.value.data
        }
        this.populateForm()
      } catch (err) {
        console.error('Error fetching profile:', err)
      }
    },
    populateForm() {
      const p = this.profileData || {}
      const u = this.userData || {}
      if (p.language || u.language) {
        this.selectedLanguage = p.language || u.language
      }
      this.form = {
        firstName: p.first_name || u.firstName || '',
        middleName: p.middle_name || u.middleName || '',
        lastName: p.last_name || u.lastName || '',
        email: p.email || u.email || '',
        phone: p.mobile || u.mobile || '',
        employeeId: u.employeeId || p.employee_id || '',
        designation: u.designation?.name || u.designation || '',
        department: u.department?.name || u.department || ''
      }
    },
    cancelEditUser() {
      this.populateForm()
      this.isEditingUser = false
    },
    async saveUserDetails() {
      try {
        this.savingUser = true
        const userId = this.userData?.userId || this.userData?._id
        await updateUserDetails({
          userId,
          employee_id: this.form.employeeId,
          firstName: this.form.firstName,
          middleName: this.form.middleName,
          lastName: this.form.lastName
        })
        this.$toast?.success?.('Profile details updated successfully!')
        this.isEditingUser = false
        this.fetchProfile()
      } catch (err) {
        console.error('Error updating details:', err)
        this.$toast?.error?.(err.response?.data?.message || 'Failed to update profile details')
      } finally {
        this.savingUser = false
      }
    },
    async updateLanguage(val) {
      try {
        await setUserLanguage(val)
        this.$toast?.success?.('Language preference saved!')
      } catch (err) {
        console.error('Language error:', err)
        this.$toast?.error?.(err.response?.data?.message || 'Failed to update language')
      }
    },
    openImageUpload() {
      this.$refs.fileInput.click()
    },
    async handlePhotoSelected(e) {
      const file = e.target.files[0]
      if (!file) return
      try {
        const formData = new FormData()
        formData.append('attachments', file)
        await updateUserProfilePic(formData)
        this.$toast?.success?.('Profile photo updated!')
        this.fetchProfile()
      } catch (err) {
        console.error('Photo error:', err)
        this.$toast?.error?.('Failed to upload photo')
      }
    },
    handleDocumentUpload(e) {
      const file = e.target.files[0]
      if (file) {
        this.selectedDocFile = file
        this.selectedDocType = file.name.split('.')[0] || ''
      }
    },
    async submitDocumentUpload() {
      if (!this.selectedDocFile || !this.selectedDocType) {
        this.$toast?.error?.('Please enter document type')
        return
      }
      try {
        this.uploadingDoc = true
        const formData = new FormData()
        formData.append('typeOfDocument', this.selectedDocType)
        formData.append('attachments', this.selectedDocFile)
        await uploadUserAttachment(formData)
        this.$toast?.success?.('Document uploaded successfully!')
        this.selectedDocFile = null
        this.fetchProfile()
      } catch (err) {
        console.error('Upload document error:', err)
        this.$toast?.error?.(err.response?.data?.message || 'Failed to upload document')
      } finally {
        this.uploadingDoc = false
      }
    },
    getIdMeta(item) {
      const typeStr = (item?.idType || '').toLowerCase()
      if (typeStr.includes('pan')) {
        return {
          type: 'pan',
          title: 'PAN Card',
          subtitle: 'Permanent Account Number'
        }
      }
      if (typeStr.includes('aadh') || typeStr.includes('adhar')) {
        return {
          type: 'aadhaar',
          title: 'Aadhaar Card',
          subtitle: 'Unique Identification Number'
        }
      }
      if (typeStr.includes('driv') || typeStr.includes('dl') || typeStr.includes('licen')) {
        return {
          type: 'dl',
          title: 'Driving License',
          subtitle: 'Driving License Number'
        }
      }
      if (typeStr.includes('vot') || typeStr.includes('epic')) {
        return {
          type: 'voter',
          title: 'Voter ID',
          subtitle: 'Election Commission of India'
        }
      }
      if (typeStr.includes('pass')) {
        return {
          type: 'passport',
          title: 'Passport',
          subtitle: 'Passport Number'
        }
      }
      return {
        type: 'generic',
        title: item?.idType || 'Identity Document',
        subtitle: 'Identification Number'
      }
    },
    formatIdNumber(item) {
      if (!item) return ''
      const num = item.idNumber || ''
      const meta = this.getIdMeta(item)
      if (meta.type === 'voter' && !num.startsWith('UID:')) {
        return `UID: ${num}`
      }
      return num
    },
    setIdentities(list) {
      if (!this.userData) {
        this.userData = {}
      }
      if (!this.userData.employmentDetails) {
        this.$set(this.userData, 'employmentDetails', {})
      }
      this.$set(this.userData.employmentDetails, 'identityDetails', list)
      this.customIdentities = list
    },
    openEditIdentity(item, index) {
      this.editingIdentityIndex = index
      this.editingIdentity = {
        ...item,
        idNumber: (item.idNumber || '').replace(/^UID:\s*/, '')
      }
      this.showEditIdentityModal = true
    },
    async saveEditedIdentity() {
      if (!this.editingIdentity?.idNumber) {
        this.$toast?.error?.('Please enter document number')
        return
      }
      try {
        this.savingIdentity = true
        const list = [...this.identityList]
        if (this.editingIdentityIndex !== null && list[this.editingIdentityIndex]) {
          list[this.editingIdentityIndex] = {
            ...list[this.editingIdentityIndex],
            idNumber: this.editingIdentity.idNumber,
            nameAsperId: this.editingIdentity.nameAsperId || list[this.editingIdentityIndex].nameAsperId || ''
          }
        }

        const employmentId = this.userData?.employmentDetails?._id
        if (employmentId) {
          await updateEmploymentDetails({
            id: employmentId,
            identityDetails: list
          })
        }

        this.setIdentities(list)
        this.$toast?.success?.('Document updated successfully!')
        this.showEditIdentityModal = false
      } catch (err) {
        console.error('Update identity error:', err)
        this.$toast?.error?.('Failed to update document')
      } finally {
        this.savingIdentity = false
      }
    },
    confirmDeleteIdentity(index) {
      this.deletingIdentityIndex = index
      this.showDeleteConfirmModal = true
    },
    async executeDeleteIdentity() {
      try {
        this.deletingIdentity = true
        const list = this.identityList.filter((_, idx) => idx !== this.deletingIdentityIndex)
        const employmentId = this.userData?.employmentDetails?._id
        if (employmentId) {
          await updateEmploymentDetails({
            id: employmentId,
            identityDetails: list
          })
        }
        this.setIdentities(list)
        this.$toast?.success?.('Document deleted successfully')
        this.showDeleteConfirmModal = false
        this.deletingIdentityIndex = null
      } catch (err) {
        console.error('Delete identity error:', err)
        this.$toast?.error?.('Failed to delete document')
      } finally {
        this.deletingIdentity = false
      }
    },
    async initiateVerify(item, index) {
      this.verifyingIndex = index
      try {
        if (item.idType === 'PAN') {
          const payload = {
            pan: item.idNumber,
            name_as_per_pan: item.nameAsperId,
            date_of_birth: dayjs(this.userData?.dob || this.userData?.personalDetails?.dob || '1990-01-01').format('DD/MM/YYYY')
          }
          const res = await verifyPanKyc(payload)
          if (res.data?.code === 200 || res.data?.data?.status === 'valid') {
            item.isVerfied = true
            const employmentId = this.userData?.employmentDetails?._id
            if (employmentId) {
              await updateEmploymentDetails({ id: employmentId, identityDetails: this.identityList })
            }
            this.setIdentities([...this.identityList])
            this.$toast?.success?.('PAN verified successfully!')
          } else {
            this.$toast?.error?.('PAN verification failed')
          }
        } else if (item.idType === 'Aadhar') {
          this.currentAadhaarItem = item
          const res = await sendAadhaarOtp({ aadhaar_number: item.idNumber })
          if (res.data?.data?.reference_id) {
            this.aadhaarRefId = res.data.data.reference_id
            this.showAadhaarOtpModal = true
            this.$toast?.success?.('Aadhaar OTP sent successfully!')
          } else {
            this.$toast?.error?.('Could not send OTP')
          }
        }
      } catch (err) {
        console.error('Verification error:', err)
        this.$toast?.error?.(err.response?.data?.message || 'Verification failed')
      } finally {
        this.verifyingIndex = null
      }
    },
    async submitAadhaarOtp() {
      try {
        this.verifyingAadhaar = true
        const res = await verifyAadhaarOtp({
          reference_id: this.aadhaarRefId,
          otp: this.aadhaarOtp
        })
        if (res.data?.code === 200 || res.data?.status === 'SUCCESS' || res.data?.success) {
          if (this.currentAadhaarItem) {
            this.currentAadhaarItem.isVerfied = true
            const employmentId = this.userData?.employmentDetails?._id
            if (employmentId) {
              await updateEmploymentDetails({ id: employmentId, identityDetails: this.identityList })
            }
            this.setIdentities([...this.identityList])
          }
          this.$toast?.success?.('Aadhaar verified successfully!')
          this.showAadhaarOtpModal = false
          this.aadhaarOtp = ''
        } else {
          this.$toast?.error?.('Invalid OTP or verification failed')
        }
      } catch (err) {
        console.error('Aadhaar error:', err)
        this.$toast?.error?.(err.response?.data?.message || 'OTP verification failed')
      } finally {
        this.verifyingAadhaar = false
      }
    },
    async submitPasscodeChange() {
      if (this.passcodeForm.passcode !== this.passcodeForm.confirmPasscode) {
        this.$toast?.error?.('New passcodes do not match')
        return
      }
      if (this.passcodeForm.passcode.length !== 6) {
        this.$toast?.error?.('Passcode must be 6 digits')
        return
      }
      try {
        this.savingPasscode = true
        await updatePasscode({
          oldPasscode: this.passcodeForm.oldPasscode,
          passcode: this.passcodeForm.passcode
        })
        this.$toast?.success?.('Passcode updated successfully!')
        this.showPasscodeModal = false
        this.passcodeForm = { oldPasscode: '', passcode: '', confirmPasscode: '' }
      } catch (err) {
        console.error('Passcode error:', err)
        this.$toast?.error?.(err.response?.data?.message || 'Failed to update passcode')
      } finally {
        this.savingPasscode = false
      }
    }
  }
}
</script>
